import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, X } from "lucide-react";
import { Product } from "@/types/product";
import ProductForm, { ProductFormData } from "./ProductForm";
import ProductTable from "./ProductTable";
import { useProductManager } from "@/hooks/useProductManager";
const ProductManagement = () => {
  const {
    products,
    loading,
    fetchProducts,
    saveProduct,
    deleteProduct,
    toggleProductStatus,
    checkAdminPermission
  } = useProductManager();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSubmit = async (formData: ProductFormData) => {
    await saveProduct(formData, editingProduct);
    handleDialogClose();
  };
  const handleEdit = (product: Product) => {
    if (!checkAdminPermission()) {
      return;
    }
    console.log("Editando produto:", product.id);
    setEditingProduct(product);
    setIsDialogOpen(true);
  };
  const handleDelete = async (product: Product) => {
    await deleteProduct(product);
  };

  const handleToggleStatus = async (product: Product) => {
    await toggleProductStatus(product);
  };
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingProduct(null);
  };
  const handleAddNewProduct = () => {
    if (!checkAdminPermission()) {
      return;
    }
    console.log("Abrindo formulário para novo produto");
    setEditingProduct(null);
    setIsDialogOpen(true);
  };
  const [selectedCategory, setSelectedCategory] = useState<string>("Todos");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const CATEGORY_FILTERS = ["Todos", "Xis", "Lanches", "Porções", "Ala Minutas", "Bebidas"] as const;

  const matchCategory = (product: Product, category: string): boolean => {
    if (category === "Todos") return true;

    const cat = (product.category || "").trim().toLowerCase();
    const name = (product.name || "").trim().toLowerCase();

    switch (category) {
      case "Xis":
        return (
          cat === "xis" ||
          name.startsWith("xis") ||
          name.includes("xis") ||
          name.startsWith("x-")
        );
      case "Lanches":
        return (
          name.includes("bauru") ||
          name.includes("dog") ||
          name.includes("cachorro") ||
          name.includes("prensado") ||
          name.includes("torrada") ||
          name.includes("torada") ||
          name.includes("hamburguer") ||
          name.includes("hambúrguer") ||
          name.includes("burger") ||
          (cat === "lanches" && !name.includes("xis") && !name.startsWith("x-"))
        );
      case "Porções":
        return (
          cat.includes("porç") ||
          cat.includes("porcao") ||
          cat.includes("porcoes") ||
          name.includes("porção") ||
          name.includes("porcao") ||
          name.includes("fritas") ||
          name.includes("isca")
        );
      case "Ala Minutas":
        return (
          cat.includes("prato") ||
          cat.includes("ala minuta") ||
          name.includes("ala minuta") ||
          name.includes("a cavalo")
        );
      case "Bebidas":
        return (
          cat.includes("bebid") ||
          name.includes("refrigerante") ||
          name.includes("cerveja") ||
          name.includes("suco") ||
          name.includes("água") ||
          name.includes("agua")
        );
      default:
        return cat === category.toLowerCase();
    }
  };

  const normalizeText = (text: string) =>
    text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const filteredProducts = products.filter((product) => {
    const matchesCat = matchCategory(product, selectedCategory);
    if (!matchesCat) return false;

    if (!searchQuery.trim()) return true;

    const query = normalizeText(searchQuery.trim());
    const name = normalizeText(product.name || "");
    const description = normalizeText(product.description || "");
    const category = normalizeText(product.category || "");

    return (
      name.includes(query) ||
      description.includes(query) ||
      category.includes(query)
    );
  });

  if (loading) {
    return <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">Carregando produtos...</p>
      </div>;
  }
  return <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Gerenciar Cardápio</h2>
          {products.length === 0 && <p className="text-muted-foreground mt-1 text-sm">
              Nenhum produto encontrado. Adicione produtos manualmente ou use o botão de importação.
            </p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {products.length === 0 && <Button onClick={() => fetchProducts()} variant="outline" className="border-border hover:bg-accent text-foreground">
              Importar Produtos Iniciais
            </Button>}
          <Button onClick={handleAddNewProduct} className="bg-krecao-red hover:bg-krecao-red/90 text-white shrink-0">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Produto
          </Button>
        </div>
      </div>

      {/* Campo de Busca */}
      <div className="relative w-full">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="text"
          placeholder="Buscar produto por nome, ingrediente ou descrição..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 pr-10 h-10 w-full rounded-lg border-border bg-background text-foreground shadow-sm focus-visible:ring-1 focus-visible:ring-krecao-red"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md"
            title="Limpar busca"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Filtro por Categorias em formato lista */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 hide-scrollbar">
        {CATEGORY_FILTERS.map((cat) => {
          const isSelected = selectedCategory === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors shrink-0 ${
                isSelected
                  ? "bg-krecao-red text-white shadow-sm"
                  : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground border border-border"
              }`}
            >
              {cat}
            </button>
          );
        })}
      </div>

      <ProductForm isOpen={isDialogOpen} onClose={handleDialogClose} onSubmit={handleSubmit} editingProduct={editingProduct} />

      <ProductTable products={filteredProducts} onEdit={handleEdit} onDelete={handleDelete} onToggleStatus={handleToggleStatus} />
    </div>;
};
export default ProductManagement;